import {
  AccountLink,
  AccountNoLink,
  TransactionLink,
} from "@/components/links";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import {
  addStakedMetaToEvents,
  HistoryEvent,
  useHistoryEvents,
} from "@/hooks/use-history-events";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import {
  capitalizeFirst,
  dateNow,
  formatNumber,
  shortenAmount,
  shortenNumber,
  showTimeDiffPartial,
} from "@/lib/utils";
import { DSContractState } from "@myth-finance/dualstake-ts-sdk";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useWallet } from "@txnlab/use-wallet";
import { cx } from "class-variance-authority";
import {
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  CircleSlash2,
  Clock,
  Ellipsis,
  Hammer,
  HammerIcon,
  Hand,
  HandCoins,
  UsersRound,
  Wrench,
} from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationFirst,
  PaginationLast,
} from "@/components/ui/pagination";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { twMerge } from "tailwind-merge";
import MyAlert from "@/components/settings/appearance";
import { Toggle } from "@/components/ui/toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const DIALOG_PAGE_SIZE = 10;

type ActivityEventRenderer = (
  e: HistoryEvent,
  cState: DSContractState
) => string | JSX.Element;

const activityIconProps = {
  className: "inline",
  size: 18,
}

const fullActivityEventRender: Record<string, ActivityEventRenderer> = {
  mint: (e: HistoryEvent, cState: DSContractState) => (
    <>
      <Row label="Sent">
        {formatNumber(e.argsByName.algo_amount as bigint)} Ⱥ
      </Row>
      <div className="flex justify-around">
        +{" "}
        <span>
          {" "}
          {shortenNumber(
            Number(e.argsByName.asa_amount_received) /
              10 ** Number(cState.asaDecimals)
          )}{" "}
          {cState.asaUnitName}
        </span>
      </div>
      <div className="flex justify-between text-muted-foreground">
        <div>Total Stake</div>
        <div>{shortenAmount(e.meta?.staked ?? 0, 6)} Ⱥ</div>
      </div>
    </>
  ),
  redeem: (e: HistoryEvent, cState: DSContractState) => (
    <>
      <Row label="Received">
        {formatNumber(e.argsByName.algo_amount as bigint)} Ⱥ
      </Row>
      <div className="flex justify-around">
        +{" "}
        <span>
          {shortenNumber(
            Number(e.argsByName.asa_amount) / 10 ** Number(cState.asaDecimals)
          )}{" "}
          {cState.asaUnitName}
        </span>
      </div>
      <div className="flex justify-between text-muted-foreground">
        <div>Total Stake</div>
        <div>{shortenAmount(e.meta?.staked ?? 0, 6)} Ⱥ</div>
      </div>
    </>
  ),
  protest: (e: HistoryEvent, _cState: DSContractState) => (
    <>
      <Row label="Lock">
        {formatNumber(e.argsByName.lst_amount as bigint)} Ⱥ
      </Row>
    </>
  ),
  unprotest: (e: HistoryEvent, _cState: DSContractState) => (
    <>
      <Row label="Unlock">
        {formatNumber(e.argsByName.lst_amount as bigint)} Ⱥ
      </Row>
    </>
  ),
  keyreg_online: (_e: HistoryEvent, _: DSContractState) => ``,
  swap: (e: HistoryEvent, cState: DSContractState) => (
    <>
      <Row label="Sold">
        {formatNumber(e.argsByName.algo_amount as bigint)} Ⱥ
      </Row>
      <Row label="Bought">
        {" "}
        {shortenNumber(
          Number(e.argsByName.asa_amount) / 10 ** Number(cState.asaDecimals)
        )}{" "}
        {cState.asaUnitName}
      </Row>
    </>
  ),
};

const activityEventRender: Record<string, ActivityEventRenderer> = {
  mint: (e: HistoryEvent, cState: DSContractState) => (
    <>
      <HammerIcon {...activityIconProps} /> Mint{" "}
      {formatNumber(e.argsByName.algo_amount as bigint)} {cState.lstName} by{" "}
      <AccountNoLink short={true} address={e.outerTransaction.sender} />
    </>
  ),
  redeem: (e: HistoryEvent, cState: DSContractState) => (
    <>
      <HandCoins {...activityIconProps} /> Redeem{" "}
      {formatNumber(e.argsByName.algo_amount as bigint)} {cState.lstName} by{" "}
      <AccountNoLink short={true} address={e.outerTransaction.sender} />
    </>
  ),
  swap: (e: HistoryEvent, cState: DSContractState) => (
    <>
      <ArrowRightLeft {...activityIconProps} /> Rewards Swap{" "}
      {formatNumber(e.argsByName.algo_amount as bigint)}A &gt;{" "}
      {shortenNumber(
        Number(e.argsByName.asa_amount) / 10 ** Number(cState.asaDecimals)
      )}{" "}
      {cState.asaUnitName}
    </>
  ),
  protest: (e: HistoryEvent, cState: DSContractState) => (
    <>
      <Hand {...activityIconProps} /> Protest{" "}
      {formatNumber(e.argsByName.lst_amount as bigint)} {cState.lstName} by{" "}
      <AccountNoLink short={true} address={e.outerTransaction.sender} />
    </>
  ),
  unprotest: (e: HistoryEvent, cState: DSContractState) => (
    <>
      <Hand {...activityIconProps} /> Unprotest{" "}
      {formatNumber(e.argsByName.lst_amount as bigint)} {cState.lstName}
    </>
  ),
  keyreg_online: (e: HistoryEvent, _: DSContractState) => (
    <>
      <Wrench {...activityIconProps} /> Key registration{" "}
      {new Date(
        ((e.outerTransaction.roundTime as number) ?? 0) * 1000
      ).toLocaleString()}
    </>
  ),
};

const activityEventNames = new Set([...Object.keys(activityEventRender)]);

const LIMIT = 6;

interface DialogFilterState {
  mint: boolean;
  redeem: boolean;
  swap: boolean;
  misc: boolean;
}

const initDialogFilterState = {
  mint: true,
  redeem: true,
  swap: true,
  misc: true,
};

export function ActivityCard({
  cState,
  className,
}: {
  className?: string;
  cState: DSContractState;
}) {
  const { activeAddress } = useWallet();
  const {
    error,
    isPending,
    refetch,
    data: appEvents,
  } = useHistoryEvents(cState?.appId);
  const [mode, setMode] = useState<"GLOBAL" | "PERSONAL">("GLOBAL");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTxnID, setSelectedTxnID] = useState("");
  const [dialogPage, setDialogPage] = useState(0);
  const [showConnectWalletAlert, setShowConnectWalletAlert] = useState(false);
  const [dialogFilterState, setDialogFilterState] = useState<DialogFilterState>(
    initDialogFilterState
  );

  useEffect(() => {
    setDialogPage(0);
  }, [mode]);

  const changePage = (n: number) => {
    setSelectedTxnID("");
    setDialogPage(n);
  };

  useEffect(() => {
    // dialog reset code
    if (!dialogOpen) setSelectedTxnID("");
    setDialogPage(0);
  }, [dialogOpen]);

  const shortActivity = useMemo(() => {
    let count = 0;
    return (
      appEvents?.events
        .map((e) => {
          if (count > LIMIT) return;
          if (!activityEventNames.has(e.spec.name)) {
            return;
          }
          if (
            mode === "PERSONAL" &&
            e.outerTransaction.sender !== activeAddress
          )
            return;
          count++;
          return e;
        })
        .filter((e) => e !== undefined) ?? []
    );
  }, [appEvents, activeAddress, mode]);

  const fullActivity = useMemo(() => {
    if (!dialogOpen) return [];
    if (!appEvents?.events.length) return [];
    return (
      addStakedMetaToEvents({
        events: appEvents.events,
        activeAddress,
        mode,
        activityEventNames,
      })
        .filter(({ spec: { name } }) => {
          if (dialogFilterState[name as keyof DialogFilterState] === false) {
            return;
          } else if (
            dialogFilterState[name as keyof DialogFilterState] === undefined &&
            !dialogFilterState.misc
          ) {
            return;
          }
          return true;
        })
        .reverse() ?? []
    );
  }, [dialogOpen, appEvents?.round, activeAddress, dialogFilterState, mode]);

  const textColors = getHighlightClasses(cState.asaId, "text");
  const borderColors = getHighlightClasses(cState.asaId, "border");

  const maxPages = Math.ceil(fullActivity.length / DIALOG_PAGE_SIZE);

  return (
    <>
      <Card className={cx("min-w-[300px] relative self-stretch", className)}>
        <CardHeader>
          <CardTitle className="opacity-0 animate-fade-in-3">
            <Clock className="inline" /> {cState.lstName} Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="opacity-0 animate-fade-in-3 min-h-32">
          <WithErrorAndLoading
            mini={true}
            loading={isPending}
            error={error?.message}
            retry={refetch}
          >
            <Tabs value={mode} onValueChange={(v) => setMode(v as any)}>
              <TabsList className="w-full mb-3 -mt-2">
                <TabsTrigger disabled={!activeAddress} value="PERSONAL">
                  Personal
                </TabsTrigger>
                <TabsTrigger value="GLOBAL">Global</TabsTrigger>
              </TabsList>
              <TabsContent key={mode} value={mode} className="max-w-full">
                {shortActivity.slice(0, LIMIT).map((e, i) => (
                  <div
                    key={`k-${i}`}
                    onClick={() => {
                      setDialogOpen(true);
                      setSelectedTxnID(e.outerTransaction.id!);
                      setDialogFilterState(initDialogFilterState);
                    }}
                    className="max-w-full truncate text-sm mb-1 text-muted-foreground cursor-pointer hover:text-foreground"
                  >
                    {activityEventRender[e.spec.name](e, cState)}
                  </div>
                ))}
                {shortActivity.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground">
                    <CircleSlash2 />
                    <div>Nothing to show</div>
                  </div>
                ) : (
                  <div className="flex mt-4 justify-center">
                    <Button
                      variant="outline"
                      className="self-center"
                      onClick={() => setDialogOpen(true)}
                    >
                      Show More
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </WithErrorAndLoading>
        </CardContent>
        <Clock
          size={192}
          strokeWidth="1.25px"
          className="absolute pointer-events-none z-0 top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] opacity-5"
        />
      </Card>
      {!dialogOpen ? null : (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <div
              className={cx("text-muted-foreground/90 self-start", className)}
            >
              <Button variant="ghost" className="text-base">
                <UsersRound className="h-4 w-4 -ml-1 mr-1 group-data-[state=open]:hidden" />
                Holders
              </Button>
            </div>
          </DialogTrigger>
          <DialogContent className="flex flex-col max-h-[100svh] overflow-y-auto">
            <DialogTitle className="text-center mb-2">
              {capitalizeFirst(mode)}{" "}
              <span className={textColors}>{cState.lstName}</span> Activity
            </DialogTitle>
            <WithErrorAndLoading
              mini={true}
              loading={false}
              loadingClassName="my-8"
              error={error?.message}
            >
              <div className="flex w-full justify-between items-center">
                <span className="text-muted-foreground text-sm">
                  Ledger: {fullActivity.length} entries
                </span>
                <MyAlert
                  show={showConnectWalletAlert}
                  setShow={setShowConnectWalletAlert}
                  title="Connect Wallet"
                  description={
                    <>
                      <p>Connect your wallet to view your personal activity.</p>
                    </>
                  }
                />
                <div className="flex items-center gap-1 text-muted-foreground">
                  <button
                    style={{ opacity: 0, height: 0 }}
                    autoFocus
                    tabIndex={-1}
                  />
                  <FilterToggles
                    activeClass={textColors}
                    state={dialogFilterState}
                    setState={setDialogFilterState}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {fullActivity
                  .slice(
                    dialogPage * DIALOG_PAGE_SIZE,
                    (dialogPage + 1) * DIALOG_PAGE_SIZE
                  )
                  .map((e) => (
                    <Collapsible
                      open={e.outerTransaction.id === selectedTxnID}
                      onOpenChange={(open: boolean) => {
                        setSelectedTxnID(open ? e.outerTransaction.id! : "");
                      }}
                      className={`group rounded-xl data-[state=open]:border ${borderColors} data-[state=open]:p-2 data-[state=open]:-mx-2`}
                    >
                      <div className="flex items-center justify-between">
                        <CollapsibleTrigger
                          className={`flex w-full max-w-full items-center gap-0 ${textColors} group-data-[state=closed]:text-muted-foreground`}
                        >
                          <Button variant="ghost" size="sm">
                            <ChevronDown className="h-4 w-4 group-data-[state=closed]:hidden" />
                            <ChevronRight className="h-4 w-4 group-data-[state=open]:hidden" />
                            <span className="sr-only">Toggle</span>
                          </Button>
                          <div className="flex w-[calc(100%-3rem)] justify-between items-center text-sm group-data-[state=open]:text-base group-data-[state=closed]:text-muted-foreground gap-x-2">
                            <div className="text-left truncate w-full">
                              {activityEventRender[e.spec.name](e, cState)}
                            </div>
                            <div className="text-right flex-shrink-0 group-data-[state=open]:hidden">
                              {showTimeDiffPartial(
                                1000 *
                                  (dateNow() -
                                    ((e.outerTransaction.roundTime as number) ??
                                      0)),
                                2,
                                true
                              )}{" "}
                              ago
                            </div>
                          </div>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent className="px-4 group-data-[state=open]:py-3 grid grid-cols-2 gap-y-1 gap-x-6">
                        <div className="flex justify-between">
                          <div className="flex justify-between w-full">
                            <span className={`font-bold ${textColors}`}>
                              {capitalizeFirst(e.spec.name).replace(/_/g, " ")}
                            </span>
                            <span className="text-right text-muted-foreground">
                              Txn{" "}
                              <TransactionLink id={e.outerTransaction.id!} />
                            </span>
                          </div>
                        </div>

                        <div className="text-right text-muted-foreground">
                          {showTimeDiffPartial(
                            1000 *
                              (dateNow() -
                                ((e.outerTransaction.roundTime as number) ??
                                  0)),
                            2,
                            true
                          )}{" "}
                          ago
                        </div>
                        <div className="flex justify-between">
                          From&nbsp;
                          <AccountLink
                            className="truncate"
                            short={true}
                            address={e.outerTransaction.sender!}
                          />
                        </div>
                        <div className="flex justify-end gap-1 text-right text-muted-foreground">
                          {new Date(
                            (e.outerTransaction.roundTime! as number) * 1000
                          ).toLocaleString()}
                        </div>

                        {fullActivityEventRender[e.spec.name](e, cState)}
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
              </div>
              {fullActivity?.length ? (
                <>
                  <div className="flex w-full justify-end gap-2 items-center">
                    <Pagination>
                      <PaginationContent className="text-sm gap-2">
                        <PaginationItem
                          className={dialogPage === 0 ? "invisible" : ""}
                        >
                          <PaginationFirst onClick={() => changePage(0)} />
                        </PaginationItem>
                        <PaginationItem
                          className={dialogPage === 0 ? "invisible" : ""}
                        >
                          <PaginationPrevious
                            onClick={() => changePage(dialogPage - 1)}
                          />
                        </PaginationItem>
                        <PaginationItem className="select-none">
                          &middot; Page {dialogPage + 1} / {maxPages} &middot;
                        </PaginationItem>
                        <PaginationItem
                          className={
                            dialogPage === maxPages - 1 ? "invisible" : ""
                          }
                        >
                          <PaginationNext
                            onClick={() => changePage(dialogPage + 1)}
                          />
                        </PaginationItem>
                        <PaginationItem
                          className={
                            dialogPage === maxPages - 1 ? "invisible" : ""
                          }
                        >
                          <PaginationLast
                            onClick={() => changePage(maxPages - 1)}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                  <div className="flex justify-between">
                    <Button
                      onClick={() => {
                        if (mode === "GLOBAL" && !activeAddress) {
                          setShowConnectWalletAlert(true);
                          return;
                        }
                        setMode((mode) =>
                          mode === "GLOBAL" ? "PERSONAL" : "GLOBAL"
                        );
                      }}
                      variant="outline"
                      className="self-end"
                      size="sm"
                    >
                      Switch to {mode === "GLOBAL" ? "Personal" : "Global"}{" "}
                      Activity
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-2 pb-8 pt-4 text-muted-foreground">
                  <CircleSlash2 />
                  <div>Nothing to show</div>
                </div>
              )}
            </WithErrorAndLoading>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export function Row({
  label,
  children,
  className,
  innerClass,
  labelClass,
}: {
  label: string | JSX.Element;
  children: React.ReactNode;
  innerClass?: string;
  className?: string;
  labelClass?: string;
}) {
  return (
    <div className={cx("flex justify-between flex-wrap w-full", className)}>
      <div className={cx("whitespace-nowrap", labelClass)}>{label}</div>
      <wbr />
      <div className={twMerge("text-right grow whitespace-nowrap", innerClass)}>
        {children}
      </div>
    </div>
  );
}

type IconProps = React.ComponentProps<typeof Hammer>;

const filterIcons: Record<
  keyof DialogFilterState,
  (props: IconProps) => JSX.Element
> = {
  mint: (props: IconProps) => <Hammer {...props} />,
  redeem: (props: IconProps) => <HandCoins {...props} />,
  swap: (props: IconProps) => <ArrowRightLeft {...props} />,
  misc: (props: IconProps) => <Ellipsis {...props} />,
};

export function FilterToggles({
  state,
  setState,
  activeClass,
}: {
  state: DialogFilterState;
  setState: Dispatch<SetStateAction<DialogFilterState>>;
  activeClass: string;
}) {
  return (
    <>
      {Object.entries(state).map(([id, enabled]) => (
        <FilterToggleSingle
          id={id}
          value={enabled}
          setValue={(newValue) => setState((s) => ({ ...s, [id]: newValue }))}
          label={`Toggle ${id} activity`}
          activeClass={activeClass}
        >
          {filterIcons[id as keyof DialogFilterState]({ className: "h-4 w-4" })}
        </FilterToggleSingle>
      ))}
    </>
  );
}

export function FilterToggleSingle({
  id,
  value,
  setValue,
  label,
  children,
  activeClass,
}: {
  id: string;
  value: boolean;
  setValue: (v: boolean) => void;
  label?: string;
  children: React.ReactNode;
  activeClass: string;
}) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger disabled={!label} asChild>
          <div>
            <Toggle
              variant="outline"
              pressed={value}
              onPressedChange={setValue}
              aria-label={label}
              className={`${activeClass} data-[state=off]:text-muted-foreground dark:data-[state=off]:text-muted-foreground`}
            >
              {children}
            </Toggle>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          {value ? "Showing" : "Not showing"} {id} activity
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
